import React, { useEffect, useMemo, useState } from "react";
import {
    StandardModal,
    StandardModalActionButton,
    StandardModalActionGroup,
    StandardModalDescription,
    StandardModalFormGroup,
    StandardModalInput,
    StandardModalLabel,
    StandardModalSectionTitle,
    Toast,
    Checkbox
} from "src/components";
import { saveSubcategory, updateSubcategory } from "../../api/subCategoryApi";
import { VISIBILITY_GROUP } from "../../constants";
import {
    DIRECTORY_LEVEL,
    getChildDirectories,
    getDirectoriesByLevel,
    getDirectoryLevel,
    getDirectoryLevelOption,
    getParentSubcategoryUuid
} from "../../directoryLevels";
import { portalColors, SegmentedButtonGroup } from "src/components/BusinessPortalComponents";
import { getVisibilityOptions } from "../../hooks/getVisibilityLabel";
import { EditorSection, HelperText,
    ParentLocationGrid,
    ParentLocationFormGroup,
    ParentLocationSelect,
    WarningText,
    ParentPickerFilters,
    ParentPickerTable,
    ParentPickerRow,
    ParentPickerCell,
    ParentPickerHeaderCell,
    ParentPickerHeaderContent,
    ParentPickerHeaderCount,
    EmptyParentPicker,
    SelectionSectionTitle,
    PreviewPath,
    PreviewArrow,
    PreviewPill,
    VisibilityDisplay,
    MixedVisibilityText,
    ParentLocationHeaderBlock
} from "../style/SubCategoriesTable.style";

export function SubcategoryEditor({ open, onClose, categories, directories, subcategory, initialDirectoryLevel }) {
    const isEditing = Boolean(subcategory?.uuid);
    const visibilityOptions = getVisibilityOptions();
    const [name, setName] = useState("");
    const [categoryUuid, setCategoryUuid] = useState("category");
    const [directoryLevel, setDirectoryLevel] = useState(initialDirectoryLevel || DIRECTORY_LEVEL.SUBCATEGORY);
    const [selectedParentSubcategory, setSelectedParentSubcategory] = useState("");
    const [selectedParentTopic, setSelectedParentTopic] = useState("");
    const [parentCategoryFilter, setParentCategoryFilter] = useState("");
    const [parentSubcategoryFilter, setParentSubcategoryFilter] = useState("");
    const [selectedParentUuids, setSelectedParentUuids] = useState([]);
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const availableCategories = useMemo(() => categories.filter(category => category.deletedAt === null), [categories]);
    const levelOption = getDirectoryLevelOption(directoryLevel);
    const selectedCategory = availableCategories.find(category => category.uuid === categoryUuid);
    const subcategoryOptions = useMemo(() =>
        categoryUuid === "category" ? [] :
            getChildDirectories(directories, categoryUuid, "", DIRECTORY_LEVEL.SUBCATEGORY),
    [categoryUuid, directories]);
    const topicOptions = useMemo(() =>
        selectedParentSubcategory === "" ? [] :
            getChildDirectories(directories, categoryUuid, selectedParentSubcategory, DIRECTORY_LEVEL.TOPIC),
    [categoryUuid, directories, selectedParentSubcategory]);

    useEffect(() => {
        const nextLevel = isEditing ?
            getDirectoryLevel(subcategory) :
            initialDirectoryLevel || DIRECTORY_LEVEL.SUBCATEGORY;
        const parentUuid = getParentSubcategoryUuid(subcategory);
        const parentTopic = directories.find(item => item.uuid === parentUuid);

        setName(subcategory?.categoryName || "");
        setCategoryUuid(subcategory?.categoryUuid || "category");
        setDirectoryLevel(nextLevel);
        setSelectedParentSubcategory(nextLevel === DIRECTORY_LEVEL.TOPIC ? parentUuid : "");
        setSelectedParentTopic(nextLevel === DIRECTORY_LEVEL.SUBTOPIC ? parentUuid : "");
        setParentCategoryFilter("");
        setParentSubcategoryFilter("");
        setSelectedParentUuids([]);

        if (nextLevel === DIRECTORY_LEVEL.SUBTOPIC && parentTopic) {
            setSelectedParentSubcategory(getParentSubcategoryUuid(parentTopic));
        }

        setErrors({});
    }, [directories, initialDirectoryLevel, subcategory, open]);

    const selectedSubcategory = subcategoryOptions.find(item => item.uuid === selectedParentSubcategory);
    const selectedTopic = topicOptions.find(item => item.uuid === selectedParentTopic);
    const selectedParentVisibleTo = selectedTopic?.visibleTo ?? selectedSubcategory?.visibleTo ?? selectedCategory?.visibleTo;
    const inheritedVisibleTo = selectedParentVisibleTo ?? subcategory?.visibleTo ?? VISIBILITY_GROUP.EMPLOYEES;
    const parentPickerRows = useMemo(() => {
        if (isEditing) return [];
        if (directoryLevel === DIRECTORY_LEVEL.SUBCATEGORY) {
            return availableCategories.map(category => ({
                uuid: category.uuid,
                name: category.name,
                parentName: visibilityOptions.find(option => option.value === category.visibleTo)?.label || "",
                visibleTo: category.visibleTo
            }));
        }

        if (directoryLevel === DIRECTORY_LEVEL.TOPIC) {
            return getDirectoriesByLevel(directories, DIRECTORY_LEVEL.SUBCATEGORY)
                .filter(parent => parentCategoryFilter === "" || parent.categoryUuid === parentCategoryFilter)
                .map(parent => ({
                    uuid: parent.uuid,
                    name: parent.categoryName,
                    parentName: availableCategories.find(category => category.uuid === parent.categoryUuid)?.name || "",
                    visibleTo: parent.visibleTo
                }));
        }

        return getDirectoriesByLevel(directories, DIRECTORY_LEVEL.TOPIC).filter(parent => {
            const categoryMatches = parentCategoryFilter === "" || parent.categoryUuid === parentCategoryFilter;
            const subcategoryMatches = parentSubcategoryFilter === "" || getParentSubcategoryUuid(parent) === parentSubcategoryFilter;
            return categoryMatches && subcategoryMatches;
        }).map(parent => {
            const parentSubcategory = directories.find(directory => directory.uuid === getParentSubcategoryUuid(parent));
            const parentCategory = availableCategories.find(category => category.uuid === parent.categoryUuid);
            return {
                uuid: parent.uuid,
                name: parent.categoryName,
                parentName: [parentSubcategory?.categoryName, parentCategory?.name].filter(Boolean).join(" > "),
                visibleTo: parent.visibleTo
            };
        });
    }, [availableCategories, directories, directoryLevel, isEditing, parentCategoryFilter, parentSubcategoryFilter, visibilityOptions]);
    const filteredSubcategoryOptions = useMemo(() =>
        parentCategoryFilter === "" ? [] :
            getChildDirectories(directories, parentCategoryFilter, "", DIRECTORY_LEVEL.SUBCATEGORY),
    [directories, parentCategoryFilter]);
    const allVisibleParentsSelected = parentPickerRows.length > 0 &&
        parentPickerRows.every(parent => selectedParentUuids.includes(parent.uuid));
    const isSelectingCategories = directoryLevel === DIRECTORY_LEVEL.SUBCATEGORY;
    const selectedParentRows = parentPickerRows.filter(parent => selectedParentUuids.includes(parent.uuid));
    const selectedParentVisibilityValues = [...new Set(
        selectedParentRows.map(parent => parent.visibleTo).filter(Boolean)
    )];
    const createVisibilityValue = selectedParentVisibilityValues.length === 1 ?
        selectedParentVisibilityValues[0] :
        undefined;
    const hasMixedVisibility = selectedParentVisibilityValues.length > 1;

    const selectedParentUuid = () => {
        if (directoryLevel === DIRECTORY_LEVEL.TOPIC) return selectedParentSubcategory;
        if (directoryLevel === DIRECTORY_LEVEL.SUBTOPIC) return selectedParentTopic;
        return "";
    };

    const validate = () => {
        const nextErrors = {};
        if (!name.trim()) nextErrors.name = `${levelOption.label} name is required.`;
        if (isEditing) {
            if (categoryUuid === "category") nextErrors.category = "Please select a category.";
            if (directoryLevel !== DIRECTORY_LEVEL.SUBCATEGORY && !selectedParentSubcategory) {
                nextErrors.parentSubcategory = "Please select a sub-category.";
            }
            if (directoryLevel === DIRECTORY_LEVEL.SUBTOPIC && !selectedParentTopic) {
                nextErrors.parentTopic = "Please select a topic.";
            }
        } else if (selectedParentUuids.length === 0) {
            nextErrors.parents = `Please select at least one parent ${directoryLevel === DIRECTORY_LEVEL.SUBCATEGORY ? "category" : directoryLevel === DIRECTORY_LEVEL.TOPIC ? "sub-category" : "topic"}.`;
        }
        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setIsLoading(true);
        const payload = isEditing ? {
            categoryName: name.trim(),
            visibleTo: inheritedVisibleTo,
            categoryUuid,
            directoryLevel,
            parentSubcategoryUuid: selectedParentUuid()
        } : {
            categoryName: name.trim(),
            visibleTo: VISIBILITY_GROUP.EMPLOYEES,
            directoryLevel,
            categoryUuids: directoryLevel === DIRECTORY_LEVEL.SUBCATEGORY ? selectedParentUuids : [],
            parentSubcategoryUuids: directoryLevel === DIRECTORY_LEVEL.SUBCATEGORY ? [] : selectedParentUuids
        };
        try {
            if (isEditing) await updateSubcategory(payload, subcategory.uuid);
            else await saveSubcategory(payload);
            Toast.success(`${levelOption.label} has been successfully ${isEditing ? "updated" : "added"}.`);
            onClose();
        } catch (error) {
            Toast.error(error.getApiErrorDetails().message);
        } finally {
            setIsLoading(false);
        }
    };

    const onCategoryChange = event => {
        setCategoryUuid(event.target.value);
        setSelectedParentSubcategory("");
        setSelectedParentTopic("");
    };

    const onParentCategoryFilterChange = event => {
        setParentCategoryFilter(event.target.value);
        setParentSubcategoryFilter("");
        setSelectedParentUuids([]);
    };

    const onParentSubcategoryFilterChange = event => {
        setParentSubcategoryFilter(event.target.value);
        setSelectedParentUuids([]);
    };

    const toggleParentUuid = uuid => {
        setSelectedParentUuids(current =>
            current.includes(uuid) ? current.filter(item => item !== uuid) : [...current, uuid]
        );
    };

    const toggleAllVisibleParents = () => {
        const visibleParentUuids = parentPickerRows.map(parent => parent.uuid);
        setSelectedParentUuids(current => {
            if (parentPickerRows.every(parent => current.includes(parent.uuid))) {
                return current.filter(uuid => !visibleParentUuids.includes(uuid));
            }

            return [...new Set([...current, ...visibleParentUuids])];
        });
    };

    const previewItems = [
        selectedCategory?.name,
        selectedSubcategory?.categoryName,
        selectedTopic?.categoryName,
        name.trim()
    ].filter(Boolean);
    const createPreviewItems = selectedParentUuids.length === 0 ?
        ["None"] :
        [`${selectedParentUuids.length} ${selectedParentUuids.length === 1 ? "parent" : "parents"} selected`];
    const displayedPreviewItems = isEditing ? previewItems : createPreviewItems;
    const parentDescription = directoryLevel === DIRECTORY_LEVEL.SUBCATEGORY ?
        "Choose one or more Categories where this Sub-Category will be added." :
        directoryLevel === DIRECTORY_LEVEL.TOPIC ?
            "Select the Category where the Topic will be added, or leave All Categories selected." :
            "Select the Category and Sub-Category where this Sub-Topic will be added.";
    const selectionTitle = directoryLevel === DIRECTORY_LEVEL.SUBCATEGORY ?
        "Select Categories" :
        directoryLevel === DIRECTORY_LEVEL.TOPIC ?
            "Select Sub-Categories" :
            "Select Topics";
    const selectionDescription = directoryLevel === DIRECTORY_LEVEL.SUBCATEGORY ?
        "Choose one or more Categories to add the Sub-Category to." :
        directoryLevel === DIRECTORY_LEVEL.TOPIC ?
            "Choose one or more Sub-Categories within this location to add the Topic to." :
            "Choose one or more Topics within this location to add the Sub-Topic to.";
    const parentNameHeader = directoryLevel === DIRECTORY_LEVEL.SUBCATEGORY ?
        "Visibility" :
        directoryLevel === DIRECTORY_LEVEL.TOPIC ?
            "Category" :
            "Sub-Category & Category";
    const pickerNameHeader = directoryLevel === DIRECTORY_LEVEL.SUBCATEGORY ?
        "Category Name" :
        levelOption.parentLabel.replace("Select ", "") + " Name";
    const visibilityDescription = directoryLevel === DIRECTORY_LEVEL.SUBCATEGORY ?
        "Visibility is inherited from the selected Category." :
        directoryLevel === DIRECTORY_LEVEL.TOPIC ?
            "Visibility is inherited from the selected Sub-Category." :
            "Visibility is inherited from the parent Sub-Category through the selected Topic.";
    const visibilityValue = isEditing ? selectedParentVisibleTo : createVisibilityValue;

    return (
        <StandardModal
            open={open}
            onClose={onClose}
            title={isEditing ? `Edit ${levelOption.label}` : levelOption.createLabel}
            description={isEditing ? `Update the ${levelOption.label} details, parent location and display settings.` : levelOption.hint}
            widthMode="large"
            bodyPadding="0"
            footer={(
                <StandardModalActionGroup $pushRight>
                    <StandardModalActionButton disabled={isLoading} onClick={onClose}>Cancel</StandardModalActionButton>
                    <StandardModalActionButton $variant="primary" disabled={isLoading} onClick={handleSubmit}>Save</StandardModalActionButton>
                </StandardModalActionGroup>
            )}
        >
            <EditorSection>
                <StandardModalFormGroup>
                    <StandardModalLabel>{levelOption.nameLabel}</StandardModalLabel>
                    <HelperText>
                        This name is displayed within the selected {directoryLevel === DIRECTORY_LEVEL.SUBCATEGORY ? "Category" : directoryLevel === DIRECTORY_LEVEL.TOPIC ? "Sub-Category" : "Topic"}.
                    </HelperText>
                    <StandardModalInput value={name} onChange={event => setName(event.target.value)} maxLength={100} background={portalColors.surface} />
                    {errors.name && <StandardModalDescription color="#D64545">{errors.name}</StandardModalDescription>}
                </StandardModalFormGroup>
            </EditorSection>

            <EditorSection>
                {isEditing ? (
                    <>
                        <ParentLocationHeaderBlock>
                            <StandardModalSectionTitle>Parent Location</StandardModalSectionTitle>
                            <HelperText>{parentDescription}</HelperText>
                        </ParentLocationHeaderBlock>
                        <ParentLocationGrid $columns={directoryLevel === DIRECTORY_LEVEL.SUBTOPIC ? "repeat(3, minmax(0, 1fr))" : directoryLevel === DIRECTORY_LEVEL.TOPIC ? "repeat(2, minmax(0, 1fr))" : "minmax(0, 15rem)"}>
                            <ParentLocationFormGroup>
                                <ParentLocationSelect value={categoryUuid} onChange={onCategoryChange} width="100%">
                                    <option value="category">Please select a category</option>
                                    {availableCategories.map(category => <option key={category.uuid} value={category.uuid}>{category.name}</option>)}
                                </ParentLocationSelect>
                                {errors.category && <StandardModalDescription color="#D64545">{errors.category}</StandardModalDescription>}
                            </ParentLocationFormGroup>

                            {directoryLevel !== DIRECTORY_LEVEL.SUBCATEGORY && (
                                <ParentLocationFormGroup>
                                    <ParentLocationSelect
                                        value={selectedParentSubcategory}
                                        onChange={event => {
                                            setSelectedParentSubcategory(event.target.value);
                                            setSelectedParentTopic("");
                                        }}
                                        width="100%"
                                        disabled={categoryUuid === "category"}
                                    >
                                        <option value="">Please select a sub-category</option>
                                        {subcategoryOptions.map(parent => <option key={parent.uuid} value={parent.uuid}>{parent.categoryName}</option>)}
                                    </ParentLocationSelect>
                                    {errors.parentSubcategory && <StandardModalDescription color="#D64545">{errors.parentSubcategory}</StandardModalDescription>}
                                </ParentLocationFormGroup>
                            )}

                            {directoryLevel === DIRECTORY_LEVEL.SUBTOPIC && (
                                <ParentLocationFormGroup>
                                    <ParentLocationSelect
                                        value={selectedParentTopic}
                                        onChange={event => setSelectedParentTopic(event.target.value)}
                                        width="100%"
                                        disabled={selectedParentSubcategory === ""}
                                    >
                                        <option value="">Please select a topic</option>
                                        {topicOptions.map(parent => <option key={parent.uuid} value={parent.uuid}>{parent.categoryName}</option>)}
                                    </ParentLocationSelect>
                                    {errors.parentTopic && <StandardModalDescription color="#D64545">{errors.parentTopic}</StandardModalDescription>}
                                </ParentLocationFormGroup>
                            )}
                        </ParentLocationGrid>
                    </>
                ) : (
                    <>
                        {directoryLevel !== DIRECTORY_LEVEL.SUBCATEGORY && (
                            <>
                                <ParentLocationHeaderBlock>
                                    <StandardModalSectionTitle>Parent Location</StandardModalSectionTitle>
                                    <HelperText>{parentDescription}</HelperText>
                                </ParentLocationHeaderBlock>

                                <ParentPickerFilters $columns={directoryLevel === DIRECTORY_LEVEL.SUBTOPIC ? "repeat(2, minmax(0, 1fr))" : "minmax(0, 16rem)"}>
                                    <ParentLocationFormGroup>
                                        <ParentLocationSelect value={parentCategoryFilter} onChange={onParentCategoryFilterChange} width="100%">
                                            <option value="">All Categories</option>
                                            {availableCategories.map(category => <option key={category.uuid} value={category.uuid}>{category.name}</option>)}
                                        </ParentLocationSelect>
                                    </ParentLocationFormGroup>

                                    {directoryLevel === DIRECTORY_LEVEL.SUBTOPIC && (
                                        <ParentLocationFormGroup>
                                            <ParentLocationSelect
                                                value={parentSubcategoryFilter}
                                                onChange={onParentSubcategoryFilterChange}
                                                width="100%"
                                            >
                                                <option value="">All Sub-Categories</option>
                                                {filteredSubcategoryOptions.map(parent => <option key={parent.uuid} value={parent.uuid}>{parent.categoryName}</option>)}
                                            </ParentLocationSelect>
                                        </ParentLocationFormGroup>
                                    )}
                                </ParentPickerFilters>
                            </>
                        )}

                        <SelectionSectionTitle>{selectionTitle}</SelectionSectionTitle>
                        <HelperText>{selectionDescription}</HelperText>
                        <ParentPickerTable $hasOverflow={parentPickerRows.length > 5}>
                            <ParentPickerRow $header $singleColumn={isSelectingCategories}>
                                <ParentPickerHeaderCell>
                                    <Checkbox
                                        checked={allVisibleParentsSelected}
                                        onChange={toggleAllVisibleParents}
                                        color={portalColors.blue}
                                    />
                                </ParentPickerHeaderCell>
                                <ParentPickerHeaderCell onClick={toggleAllVisibleParents}>
                                    <ParentPickerHeaderContent>
                                        <span>{pickerNameHeader}</span>
                                        {isSelectingCategories && (
                                            <ParentPickerHeaderCount>
                                                {parentPickerRows.length} Available
                                            </ParentPickerHeaderCount>
                                        )}
                                    </ParentPickerHeaderContent>
                                </ParentPickerHeaderCell>
                                {!isSelectingCategories && (
                                    <ParentPickerHeaderCell onClick={toggleAllVisibleParents}>
                                        {parentNameHeader}
                                    </ParentPickerHeaderCell>
                                )}
                            </ParentPickerRow>
                            {parentPickerRows.length === 0 ? (
                                <EmptyParentPicker>No available parents found.</EmptyParentPicker>
                            ) : parentPickerRows.map(parent => (
                                <ParentPickerRow key={parent.uuid} $singleColumn={isSelectingCategories}>
                                    <ParentPickerCell>
                                        <Checkbox
                                            checked={selectedParentUuids.includes(parent.uuid)}
                                            onChange={() => toggleParentUuid(parent.uuid)}
                                            color={portalColors.blue}
                                        />
                                    </ParentPickerCell>
                                    <ParentPickerCell title={parent.name}>{parent.name}</ParentPickerCell>
                                    {!isSelectingCategories && (
                                        <ParentPickerCell title={parent.parentName}>{parent.parentName || "-"}</ParentPickerCell>
                                    )}
                                </ParentPickerRow>
                            ))}
                        </ParentPickerTable>
                        {errors.parents && <StandardModalDescription color="#D64545">{errors.parents}</StandardModalDescription>}
                    </>
                )}

                {isEditing && (
                    <WarningText>
                        Moving this {levelOption.label} will also move all content assigned to it.
                    </WarningText>
                )}

                <StandardModalSectionTitle>Location Preview</StandardModalSectionTitle>
                <PreviewPath>
                    {displayedPreviewItems.map((item, index) => (
                        <React.Fragment key={`${item}_${index}`}>
                            {index > 0 && <PreviewArrow>&gt;</PreviewArrow>}
                            <PreviewPill>{item}</PreviewPill>
                        </React.Fragment>
                    ))}
                </PreviewPath>
            </EditorSection>

            <EditorSection>
                <StandardModalSectionTitle>Visibility</StandardModalSectionTitle>
                <HelperText>
                    {visibilityDescription}
                </HelperText>
                <VisibilityDisplay $hasValue={Boolean(visibilityValue)}>
                    <SegmentedButtonGroup
                        options={visibilityOptions}
                        value={visibilityValue}
                        equalWidth
                        onChange={() => {}}
                    />
                </VisibilityDisplay>
                {hasMixedVisibility && (
                    <MixedVisibilityText>
                        Selected parents have different visibility settings and each new item will inherit from its own parent.
                    </MixedVisibilityText>
                )}
            </EditorSection>
        </StandardModal>
    );
}
